import get from 'lodash/get'
import { Chunk, FindChunks } from 'react-highlight-words'
import { toast } from 'react-toastify'
import { IEsGlobalSearchItem } from 'types/eslasticSearch'
import { addAllStepOfProcessToCommonLibrary } from 'API/commonLibrary'
import { getUserPermissionsOnCollections } from 'API/user'
import { EEsGlobalSearchCollection } from 'constants/elasticSearch'
import { IGroupMemberPermissionId } from 'interfaces/group'
import { IGroupMember } from 'interfaces/group'

// *INFO: A custom findChunks function that returns chunks for whole words only
export const findFullWordChunks: (options: FindChunks) => Chunk[] = ({ searchWords, textToHighlight }) => {
  // *INFO: An array to store chunks of the text that will be highlighted
  const chunks: Chunk[] = []
  const textInLowerCase: string = textToHighlight.toLowerCase()

  // *INFO: A regular expression that matches word boundaries
  const wordBoundaryRegExp: RegExp = /\b/

  // *INFO: An array of words in the lowercase text
  const individualWordsInText: string[] = textInLowerCase.split(wordBoundaryRegExp)

  let startingIndexTracker: number = 0

  // *INFO: An array of objects storing the word, its starting index, and its length
  const individualWordsWithPositionAndLength: {
    word: string
    index: number
    length: number
  }[] = individualWordsInText.map(individualWord => {
    const indexOfWordInText: number = textInLowerCase.indexOf(individualWord, startingIndexTracker)
    startingIndexTracker = indexOfWordInText
    return {
      word: individualWord,
      index: indexOfWordInText,
      length: individualWord.length
    }
  })

  // *INFO: Loop through each word in the searchWords array
  searchWords
    .join(' ')
    .split(wordBoundaryRegExp)
    .forEach(searchWord => {
      // *INFO: Lowercase version of the current search word
      const searchWordInLowerCase: string = String(searchWord).toLowerCase()

      // *INFO: Loop through each individual word in the text with its position and length
      individualWordsWithPositionAndLength.forEach(individualWordWithPositionAndLength => {
        // *INFO: If the current individual word is equal to the current search word
        if (individualWordWithPositionAndLength.word === searchWordInLowerCase) {
          // *INFO: Calculate the start and end indices of the current individual word
          const startIndex: number = individualWordWithPositionAndLength.index
          const endIndex: number =
            individualWordWithPositionAndLength.index + individualWordWithPositionAndLength.length

          // *INFO: Push a chunk for the current individual word to the chunks array
          chunks.push({
            start: startIndex,
            end: endIndex
          })
        }
      })
    })

  // *INFO: Return the final chunks array
  return chunks
}

function checkUserPermissions(userPermissions: IGroupMember[]) {
  return userPermissions?.some(userPermission => userPermission?.permissionId === IGroupMemberPermissionId.EDITOR)
}

export async function isUserHavePermissionForCollection(userId: number, isBasicMember: boolean, collectionId: number) {
  if (!isBasicMember) return true
  const data = await getUserPermissionsOnCollections(userId, [collectionId])
  return checkUserPermissions(get(data, '[0].userPermissions', []) as IGroupMember[])
}

/**
 * Which of the selected rows the viewer may only look at, so the bulk buttons are
 * not offered for them.
 *
 * Collections only: who may act on a Collection is still decided by the Group
 * permissions this asks for. A Stack is not, any more — `getStackCapabilities`
 * answers per row from the Stack itself, and a Player holds no capability on one,
 * so asking the old per-user permission endpoint about Stacks decided nothing.
 */
export async function updateViewOnlyItemsAfterSelectAll(
  userId: number,
  items: IEsGlobalSearchItem[],
  setViewOnlyProcess: (viewOnlyProcessIds: number[]) => void,
  isBasicUser: boolean = false,
  setViewOnlyCollection?: (viewOnlyCollectionIds: number[]) => void
) {
  const collectionIds: number[] = []
  setViewOnlyProcess([])
  setViewOnlyCollection?.([])

  items.forEach(item => {
    if (item.collectionName !== EEsGlobalSearchCollection.PROCESS) {
      collectionIds.push(item.correlatingId)
    }
  })

  if (isBasicUser) {
    const collectionData = await getUserPermissionsOnCollections(userId, collectionIds)

    setViewOnlyCollection?.(
      collectionData
        .map(permission => (!checkUserPermissions(permission?.userPermissions ?? []) ? permission?.id : 0))
        .filter(Boolean)
    )
  }
}

export async function addAllStepsOfProcessToCommonLibrary(
  processId: number,
  closeModal: () => void,
  refetch: () => void
) {
  if (!processId) return
  try {
    await addAllStepOfProcessToCommonLibrary(processId)
    refetch()
    closeModal()
    toast.success('Added all steps to common library')
  } catch (error: any) {
    toast.error(`${error}`)
  }
}

export function mapTitleToValueSearchOrder(value: string) {
  switch (value) {
    case 'Last updated':
      return 'updatedAt desc'
    case 'Most relevant':
      return 'relevant'
    case 'Most viewed':
      return 'view desc'
    case 'Newest':
      return 'publishedDate desc'
    case 'Name (A-Z)':
      return 'name.keyword asc'
    default:
      return 'Most relevant'
  }
}
